import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiMessageSquare, FiChevronDown, FiChevronRight, FiCheck } from "react-icons/fi";
import { setModalState, modalViewsConst } from "../../store/modalSlice";
import {
  fetchProjectConversations,
  clearProjectUnread,
  deleteProjectConversation,
  fetchProjectTasks,
  markTaskComplete,
} from "../../store/projectChatSlice";
import type { AppDispatch, RootState } from "../../store";

interface ChatHistoryWidgetProps {
  projectId: string;
  projectTitle: string;
}

// Result the generalDelete modal reports back via modalState.lastResult
interface DeleteModalResult {
  confirmed?: boolean;
  itemId?: string;
  itemType?: 'conversation' | 'schedule' | 'processedModelSchema' | 'project';
}

const ChatHistoryWidget: React.FC<ChatHistoryWidgetProps> = ({ projectId, projectTitle }) => {
  const dispatch = useDispatch<AppDispatch>();
  const conversations = useSelector((state: RootState) => state.projectChat.conversations);
  const conversationsLoading = useSelector((state: RootState) => state.projectChat.conversationsLoading);
  const activeConversation = useSelector((state: RootState) => state.projectChat.currentConversation);
  const activeMessageCount = useSelector((state: RootState) =>
    state.projectChat.messages.filter((m) => !m.id.endsWith("-pending")).length
  );
  const unreadCounts = useSelector((state: RootState) => state.projectChat.unreadCounts);
  const tasks = useSelector((state: RootState) => state.projectChat.tasks);
  const lastResult = useSelector((state: RootState) => state.modalState.lastResult);
  const handledResultRef = useRef<DeleteModalResult | null>(null);

  const [tasksExpanded, setTasksExpanded] = useState(true);
  const [chatsExpanded, setChatsExpanded] = useState(true);
  const [completedTasksExpanded, setCompletedTasksExpanded] = useState(false);
  const [expandedTaskIds, setExpandedTaskIds] = useState<string[]>([]);

  useEffect(() => {
    dispatch(fetchProjectConversations(projectId));
    dispatch(fetchProjectTasks(projectId));
  }, [dispatch, projectId]);

  // Handle generalDelete modal confirmation
  useEffect(() => {
    if (
      lastResult?.confirmed === true &&
      lastResult?.itemId &&
      lastResult !== handledResultRef.current
    ) {
      handledResultRef.current = lastResult;
      dispatch(deleteProjectConversation(lastResult.itemId));
    }
  }, [lastResult, dispatch]);

  const toggleTaskExpand = (taskId: string) => {
    setExpandedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const openModal = (initialConversationId?: string, taskId?: string) => {
    if (initialConversationId) dispatch(clearProjectUnread(initialConversationId));
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.projectChat,
      properties: {
        __modalSize: "2xl",
        projectId,
        projectTitle,
        ...(initialConversationId && { initialConversationId }),
        ...(taskId && { projectTaskId: taskId }),
      },
    }));
  };

  const handleDeleteClick = (conversationId: string, conversationTitle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.generalDelete,
      properties: {
        __modalSize: "sm",
        title: "Delete Conversation",
        itemName: conversationTitle,
        itemId: conversationId,
      },
    }));
  };

  const handleMarkDone = (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(markTaskComplete(taskId));
  };

  const isLoading = conversationsLoading === "pending";

  // Split conversations into task-linked vs free chats, then bucket tasks — memoized to avoid re-filtering on every render
  const { taskConversations, freeChats, activeTasks, completedTasks } = useMemo(() => {
    const taskConvs = conversations.filter((c) => c.projectTaskId);
    const linkedTaskIds = new Set(taskConvs.map((c) => c.projectTaskId));
    return {
      taskConversations: taskConvs,
      freeChats: conversations.filter((c) => !c.projectTaskId),
      activeTasks: tasks.filter((t) => !t.completedTask && linkedTaskIds.has(t.id)),
      completedTasks: tasks.filter((t) => t.completedTask && linkedTaskIds.has(t.id)),
    };
  }, [conversations, tasks]);

  const renderConversationRow = (conv: typeof conversations[0]) => (
    <div
      key={conv.id}
      onClick={() => openModal(conv.id)}
      className={`cursor-pointer text-left w-full rounded-md border px-2 py-2 hover:bg-gray-100 transition group flex items-center gap-2 ${
        conv.id === activeConversation?.id
          ? "bg-eai-checklist-lt border-eai-primary-accent"
          : "border-gray-200"
      }`}
    >
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-gray-900 truncate">{conv.title}</span>
        <span className="block text-[11px] text-gray-400">
          {conv.id === activeConversation?.id ? activeMessageCount : conv.messageCount} messages
        </span>
      </span>
      <button
        onClick={(e) => handleDeleteClick(conv.id, conv.title, e)}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-red-100 rounded flex-shrink-0"
        title="Delete conversation"
      >
        <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
      {(unreadCounts[conv.id] ?? 0) > 0 && (
        <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded text-[10px] font-bold bg-eai-primary-accent text-white">
          {(unreadCounts[conv.id] ?? 0) > 9 ? "9+" : unreadCounts[conv.id]}
        </span>
      )}
    </div>
  );

  return (
    <div className="border border-gray-200 rounded-md p-4 flex flex-col gap-3 h-[36rem] lg:h-auto lg:min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <FiMessageSquare className="w-4 h-4 text-eai-primary-accent" />
          Chat History
        </div>
        <button onClick={() => openModal()} className="eai-btn-grn text-sm">
          + New Chat
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-eai-primary-accent border-t-transparent" />
        </div>
      ) : (
        <div className="flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto">

          {/* ── TASKS section ── */}
          <div>
            <button
              onClick={() => setTasksExpanded((v) => !v)}
              className="w-full flex items-center gap-2 py-1 group"
            >
              <span className="text-gray-400">
                {tasksExpanded ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />}
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 group-hover:text-gray-600 transition-colors">
                Tasks
              </span>
              {activeTasks.length > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded text-[10px] font-bold bg-eai-primary-accent text-white">
                  {activeTasks.length}
                </span>
              )}
              <span className="flex-1 h-px bg-gray-200 ml-1" />
            </button>

            {tasksExpanded && (
              <div className="mt-1 space-y-1">
                {tasks.length === 0 ? (
                  <p className="px-2 py-1 text-xs text-gray-400 italic">No tasks yet</p>
                ) : (
                  <>
                    {activeTasks.map((task) => {
                      const taskConvs = taskConversations.filter((c) => c.projectTaskId === task.id);
                      const isOpen = expandedTaskIds.includes(task.id);
                      return (
                        <div key={task.id} className="rounded-md border border-gray-200">
                          {/* Task row */}
                          <div
                            onClick={() => toggleTaskExpand(task.id)}
                            className="cursor-pointer w-full flex items-center gap-2 px-2 py-2 hover:bg-gray-100 rounded-md group"
                          >
                            <span className="text-gray-400 flex-shrink-0">
                              {isOpen ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                            </span>
                            <span className="text-sm font-medium text-gray-900 truncate flex-1 text-left">
                              {task.title}
                            </span>
                            {taskConvs.length > 0 && (
                              <span className="text-[10px] text-gray-400 flex-shrink-0">
                                {taskConvs.length}
                              </span>
                            )}
                            <div className="relative opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 group/check">
                              <button
                                onClick={(e) => handleMarkDone(task.id, e)}
                                className="p-0.5 hover:bg-eai-primary-accent hover:text-white rounded"
                              >
                                <FiCheck size={12} className="text-eai-primary-accent group-hover/check:text-white" />
                              </button>
                              <span className="pointer-events-none absolute right-full top-1/2 -translate-y-1/2 mr-1.5 whitespace-nowrap rounded bg-gray-700 px-1.5 py-0.5 text-[10px] text-white opacity-0 group-hover/check:opacity-100 transition-opacity">
                                Mark done
                              </span>
                            </div>
                          </div>

                          {/* Task conversations */}
                          {isOpen && (
                            <div className="px-2 pb-2 space-y-1">
                              {taskConvs.length === 0 ? (
                                <p className="text-[11px] text-gray-400 italic py-1">No chats yet</p>
                              ) : (
                                taskConvs.map(renderConversationRow)
                              )}
                              <button
                                onClick={() => openModal(undefined, task.id)}
                                className="eai-btn-grn text-sm"
                              >
                                + New Chat
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Completed tasks — collapsible sub-section */}
                    {completedTasks.length > 0 && (
                      <div className="mt-1">
                        <button
                          onClick={() => setCompletedTasksExpanded((v) => !v)}
                          className="w-full flex items-center gap-1.5 px-1 py-1 group"
                        >
                          <span className="text-gray-300">
                            {completedTasksExpanded ? <FiChevronDown size={11} /> : <FiChevronRight size={11} />}
                          </span>
                          <span className="text-[10px] text-gray-400 group-hover:text-gray-500 transition-colors">
                            Completed ({completedTasks.length})
                          </span>
                        </button>
                        {completedTasksExpanded && (
                          <div className="space-y-1 mt-0.5">
                            {completedTasks.map((task) => {
                              const taskConvs = taskConversations.filter((c) => c.projectTaskId === task.id);
                              const isOpen = expandedTaskIds.includes(task.id);
                              return (
                                <div key={task.id} className="rounded-md border border-gray-100 opacity-60">
                                  <button
                                    onClick={() => toggleTaskExpand(task.id)}
                                    className="w-full flex items-center gap-2 px-2 py-2 hover:bg-gray-100 rounded-md group"
                                  >
                                    <span className="text-gray-300 flex-shrink-0">
                                      {isOpen ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />}
                                    </span>
                                    <FiCheck size={11} className="text-green-500 flex-shrink-0" />
                                    <span className="text-sm font-medium text-gray-400 truncate flex-1 text-left line-through">
                                      {task.title}
                                    </span>
                                    {taskConvs.length > 0 && (
                                      <span className="text-[10px] text-gray-300 flex-shrink-0">
                                        {taskConvs.length}
                                      </span>
                                    )}
                                  </button>
                                  {isOpen && taskConvs.length > 0 && (
                                    <div className="px-2 pb-2 space-y-1">
                                      {taskConvs.map(renderConversationRow)}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── CHATS section ── */}
          <div className="mt-1">
            <button
              onClick={() => setChatsExpanded((v) => !v)}
              className="w-full flex items-center gap-2 py-1 group"
            >
              <span className="text-gray-400">
                {chatsExpanded ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />}
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 group-hover:text-gray-600 transition-colors">
                Chats
              </span>
              {freeChats.length > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded text-[10px] font-bold bg-gray-400 text-white">
                  {freeChats.length}
                </span>
              )}
              <span className="flex-1 h-px bg-gray-200 ml-1" />
            </button>

            {chatsExpanded && (
              <div className="mt-1 space-y-1">
                {freeChats.length === 0 ? (
                  <p className="px-2 py-1 text-xs text-gray-400 italic">No chats yet</p>
                ) : (
                  freeChats.map(renderConversationRow)
                )}
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

export default ChatHistoryWidget;
